import { Component } from '../Component';
import { ArgSpecDictionary } from '../ComponentUtils';

import swiperStyles from '../../../node_modules/swiper/swiper-bundle.min.css?inline';
import Swiper from 'swiper';
import { Pagination, Navigation } from 'swiper/modules'; // Import Pagination module
import { CSSBreakpoint } from '../../common/constants-theme';
import { siteConfig } from '../../common/common';
import { rosAnimation, STAnimationType } from '../../utils/ScrollTriggerHelper';

const DEBUG_VERBOSE = false;
export const HHDSFilteredGalleryTagName = 'hhds-filtered-gallery';
const TAG_NAME = HHDSFilteredGalleryTagName;
const LOG_PREFIX = `[${TAG_NAME}]`;

export const HHDSFilteredGalleryAttrNames = {};

// const Attrs = HHDSFilteredGalleryAttrNames;

const CategoryAll = 'all';

enum CSSClass {
	IsActive = 'is-active',
	SwiperSlide = 'swiper-slide',
	FinalSlide = 'final-slide',
}

export class HHDSFilteredGallery extends Component {
	private menuItemEls: HTMLButtonElement[] = [];

	private swiperContainer: HTMLElement | null = null;
	private swiperInstance: Swiper | null = null;

	private filteredDataEls: HTMLElement[] = [];

	constructor() {
		super();
	}

	protected override init(): void {
		DEBUG_VERBOSE && logger.log(LOG_PREFIX, 'init');

		const html: string = /* html */ `
      <div class="${TAG_NAME}">
				<slot></slot>
				<div class="swiper">
					<div class="swiper-wrapper"></div>
				</div>
				<div class="${TAG_NAME}-centred">
					<div class="${TAG_NAME}__ui">
						<hhds-button-pagination direction="previous"></hhds-button-pagination>
						<span class="${TAG_NAME}__ui-pagination"></span>
						<hhds-button-pagination direction="next"></hhds-button-pagination>
					</div>
				</div>
				<slot name="data"></slot>
			</div>
    `;

		this.setInnerHtml('', '', html, [swiperStyles]);

		this.observeSlotChanges(true);

		// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

		this.initElements();

		this.initMenu();
	}

	// ////////////////////////////////////////////////////////////////////

	swiperInit() {
		DEBUG_VERBOSE && logger.log(LOG_PREFIX, this.swiperInit.name);

		if (!this.swiperContainer) throw new Error('No swiperContainer');

		// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

		// Ensure we clean up Swiper if it already exists as can cause issues
		if (this.swiperInstance) this.swiperDestroy();

		// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
		/**
		 * Define out pagination and navigation elements
		 *
		 * We need to specifically query this as just using selectors can cause
		 * issues when multiple carousels are on the same page
		 */

		const btnPreviousEl = this.querySelector('hhds-button-pagination[direction="previous"]') as HTMLElement;
		const btnNextEl = this.querySelector('hhds-button-pagination[direction="next"]') as HTMLElement;
		const paginationEl = this.querySelector(`.${TAG_NAME}__ui-pagination`) as HTMLElement;

		if (!btnPreviousEl || !btnNextEl || !paginationEl) throw new Error('Unable to derive pagination elements!');

		// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

		this.swiperInstance = new Swiper(this.swiperContainer, {
			modules: [Pagination, Navigation],
			loop: true,
			slideFullyVisibleClass: 'swiper-slide-visible',
			slidesPerView: 'auto',
			spaceBetween: 8,
			lazyPreloadPrevNext: 1,
			centeredSlides: true,
			speed: 1150,
			loopPreventsSliding: true,
			breakpoints: {
				// when window width is >=
				[CSSBreakpoint.MD]: {
					spaceBetween: 20,
				},
				// when window width is >=
				[CSSBreakpoint.LG]: {
					spaceBetween: 24,
				},
			},

			// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

			pagination: {
				el: paginationEl,
				type: 'custom',
				renderCustom: (_swiper, current, total) => {
					const formatNumber = (num: number) => num.toString().padStart(2, '0');

					return `${formatNumber(current)} / ${formatNumber(total)}`;
				},
			},

			// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

			navigation: {
				nextEl: btnNextEl,
				prevEl: btnPreviousEl,
			},

			// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

			on: {
				init: (swiper: Swiper) => {
					if (siteConfig.enableRevealOnScroll && siteConfig.autoStartCarousel) {
						swiper.slides.forEach((slide, index) => {
							// Find the slide marked as the final-slide and move to it.
							// In loop mode, slides are repeated so we can't just count
							// the initial elements added.
							if (slide.classList.contains(CSSClass.FinalSlide)) {
								swiper.slideTo(index, 0, false);
							}
						});
					}
				},
				slideChange: (_swiper: Swiper) => {
					/*this.setButtonDisabledState();
          this.handleVisibleSlides();
          DEBUG_VERBOSE && logger.log("slideChange", swiper);*/
				},
				resize: () => {
					//this.handleVisibleSlides();
				},
			},
		});

		this.swiperInstance.init();

		// If ROS animation is enabled, start on the final slide,
		// and slide to the first when reaching the appropriate reveal
		// y-position on the page.
		if (siteConfig.enableRevealOnScroll && siteConfig.autoStartCarousel) {
			rosAnimation(this, STAnimationType.triggered, {
				start: '65%',
				end: '100%',
				onStart: () => {
					this.swiperInstance?.slideNext();
				},
			});
		}
	}

	swiperDestroy() {
		DEBUG_VERBOSE && logger.log(LOG_PREFIX, this.swiperDestroy.name);
		this.swiperInstance?.destroy(true, true);
		this.swiperInstance = null;
	}

	// ////////////////////////////////////////////////////////////////////

	private initElements() {
		DEBUG_VERBOSE && logger.log(LOG_PREFIX, this.initElements.name);

		this.swiperContainer = this.querySelector('.swiper') as HTMLElement;

		if (!this.swiperContainer) throw new Error('Unable to derive swiperContainer');
	}

	// ////////////////////////////////////////////////////////////////////

	private initMenu() {
		DEBUG_VERBOSE && logger.log(LOG_PREFIX, this.initMenu.name);

		this.menuItemEls = Array.from(this.querySelectorAll(`.${TAG_NAME}__menu button`)) as HTMLButtonElement[];

		this.menuItemEls.forEach((menuItemEl) => {
			const { category } = menuItemEl.dataset;

			if (!category) throw new Error('Unable to determine category!');

			// -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> ->

			menuItemEl.onclick = () => {
				DEBUG_VERBOSE && logger.log(LOG_PREFIX, { category });
				this.updateContent(category);
			};
		});
	}

	private updateContent(nextCategory: string) {
		DEBUG_VERBOSE && logger.log(LOG_PREFIX, this.updateContent.name, { nextCategory });

		// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
		// Update active menu item

		this.menuItemEls.forEach((menuItemEl) => {
			const { category } = menuItemEl.dataset;
			menuItemEl.classList.toggle(CSSClass.IsActive, category === nextCategory);
		});

		// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

		const selector = nextCategory === CategoryAll ? `[slot="data"] > *` : `[slot="data"] > *[data-category="${nextCategory}"]`;

		this.filteredDataEls = Array.from(this.querySelectorAll(selector)) as HTMLElement[];

		// Copy filtered items into Swiper

		const swiperWrapperSelector = '.swiper-wrapper';
		const swiperWrapper = this.querySelector(swiperWrapperSelector);
		if (!swiperWrapper) throw new Error(`Unable to derive selector: ${swiperWrapperSelector}`);

		// -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> -> ->

		swiperWrapper.innerHTML = '';

		this.filteredDataEls.forEach((filteredDataEl, index) => {
			const { src, srcset, sizes, alt } = filteredDataEl.dataset;

			const classes = [CSSClass.SwiperSlide];

			let imageMarkup = `<hhds-image`;
			if (src) imageMarkup += ` src="${src}"`;
			if (srcset) imageMarkup += ` srcset="${srcset}"`;
			if (sizes) imageMarkup += ` sizes="${sizes}"`;
			if (alt) imageMarkup += ` alt="${alt}"`;

			if (index == this.filteredDataEls.length - 1) classes.push(CSSClass.FinalSlide);

			imageMarkup += ` class="${classes.join(' ')}"`;

			imageMarkup += `>`;
			imageMarkup += filteredDataEl.innerHTML; // Copt the slot markup
			imageMarkup += `</hhds-image>`;

			const template = document.createElement('template');
			template.innerHTML = imageMarkup.trim();

			const element = template.content.firstElementChild;

			if (element) {
				swiperWrapper.appendChild(element);
			}
		});

		this.swiperInit();
	}

	// ////////////////////////////////////////////////////////////////////

	protected override destroy(): void {
		DEBUG_VERBOSE && logger.log(LOG_PREFIX, 'destroy');
	}

	override onAttributeChanged(name: string, _oldValue: string, _newValue: string): void {
		DEBUG_VERBOSE && logger.log(LOG_PREFIX, `onAttributeChanged: ${name}`);
		this.reinit();
	}

	override onSlotChange(_slot: HTMLSlotElement, _elements: HTMLElement[]): void {
		DEBUG_VERBOSE && logger.log(LOG_PREFIX, `onSlotChange`);

		this.updateContent(CategoryAll);
	}

	static override argSpecs(): ArgSpecDictionary {
		return ArgSpecs;
	}
}

export const ArgSpecs: ArgSpecDictionary = {};
